import { useState, useCallback, useEffect } from 'react';

const CONSENT_KEY = 'nxt_cookie_consent';
const REVOKE_NOTICE_KEY = 'nxt_consent_revoked';
const GA_ID = 'G-3BSB28BLFT';
const GA_SCRIPT_SELECTOR = 'script[src*="googletagmanager.com/gtag"]';

/**
 * A rejection expires after 6 months, the minimum the Garante Privacy allows before a
 * banner may be shown again. An acceptance never expires — re-prompting someone who
 * already said yes only risks turning a granted consent into a withdrawn one.
 */
const REJECTION_TTL_MS = 180 * 24 * 60 * 60 * 1000;

export type ConsentStatus = 'pending' | 'accepted' | 'rejected';

type StoredConsent = { status: 'accepted' | 'rejected'; ts: number };

const readStoredConsent = (): ConsentStatus => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return 'pending';
    // Legacy format: consent was stored as the bare string, with no timestamp.
    if (raw === 'accepted') return 'accepted';

    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.status === 'accepted') return 'accepted';
    if (parsed.status !== 'rejected') return 'pending';
    // Treat an unusable timestamp as a fresh rejection rather than letting NaN
    // comparisons make it permanent.
    if (typeof parsed.ts !== 'number' || !Number.isFinite(parsed.ts)) return 'rejected';
    // A stored rejection lapses back to 'pending', which re-shows the banner.
    return Date.now() - parsed.ts > REJECTION_TTL_MS ? 'pending' : 'rejected';
  } catch {
    return 'pending';
  }
};

const writeStoredConsent = (status: 'accepted' | 'rejected') => {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ status, ts: Date.now() }));
  } catch {
    // Storage unavailable (private mode, quota) — decision holds for this page only.
  }
};

function gtag(...args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  // gtag.js reads each queued item as an `arguments` object, not as a plain array.
  function push() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  }
  (push as (...forwarded: unknown[]) => void)(...args);
}

const isGALoaded = () => document.querySelector(GA_SCRIPT_SELECTOR) !== null;

/**
 * Every domain a cookie could have been scoped to, from the current host up to the
 * registrable domain. GA4 writes `_ga` on the highest registrable domain, so on
 * `www.example.com` the cookie lives on `.example.com` and deleting it against the
 * full hostname silently does nothing.
 */
const cookieDomains = (): (string | null)[] => {
  const parts = window.location.hostname.split('.');
  const domains: (string | null)[] = [null]; // host-only cookie, no Domain attribute
  for (let i = 0; i < parts.length - 1; i++) {
    const domain = parts.slice(i).join('.');
    domains.push(domain, '.' + domain);
  }
  return domains;
};

const isGACookie = (name: string) =>
  name === '_ga' ||
  name === '_gid' ||
  name.startsWith('_ga_') ||
  name.startsWith('_gat') ||
  name.startsWith('_gac_');

/**
 * Manages cookie consent and conditionally loads Google Analytics.
 * GA4 is loaded ONLY after explicit user consent (GDPR / ePrivacy).
 */
export const useCookieConsent = () => {
  const [status, setStatus] = useState<ConsentStatus>(readStoredConsent);

  const loadGA = useCallback(() => {
    if (isGALoaded()) return;

    // Declare the denied default before gtag.js evaluates, then grant — so the tag
    // never has a window in which it may write storage without consent.
    gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    });
    gtag('consent', 'update', { analytics_storage: 'granted' });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(script);

    gtag('js', new Date());
    gtag('config', GA_ID);
  }, []);

  const removeGACookies = useCallback(() => {
    const domains = cookieDomains();
    for (const cookie of document.cookie.split(';')) {
      const name = cookie.split('=')[0].trim();
      if (!isGACookie(name)) continue;
      for (const domain of domains) {
        const scope = domain ? `;domain=${domain}` : '';
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${scope}`;
      }
    }
  }, []);

  useEffect(() => {
    if (status === 'accepted') loadGA();
  }, [status, loadGA]);

  const accept = useCallback(() => {
    writeStoredConsent('accepted');
    setStatus('accepted');
    loadGA();
  }, [loadGA]);

  /**
   * Withdraws consent. Tells the tag to stop writing storage, clears what it wrote,
   * and reloads when gtag.js is already running — detaching the <script> element does
   * not unload the code it evaluated, which would otherwise keep collecting until the
   * next navigation.
   */
  const withdraw = useCallback(() => {
    const gaWasRunning = isGALoaded();

    if (gaWasRunning) {
      gtag('consent', 'update', { analytics_storage: 'denied' });
      document.querySelector(GA_SCRIPT_SELECTOR)?.remove();
    }

    removeGACookies();
    writeStoredConsent('rejected');
    setStatus('rejected');

    if (gaWasRunning) {
      try {
        sessionStorage.setItem(REVOKE_NOTICE_KEY, '1');
      } catch {
        // Non-fatal: the confirmation message is cosmetic.
      }
      window.location.reload();
    }
  }, [removeGACookies]);

  return { status, accept, reject: withdraw, revoke: withdraw };
};

/** Consumes the one-shot "consent was revoked" flag left behind by a reload. */
export const consumeRevokeNotice = (): boolean => {
  try {
    if (sessionStorage.getItem(REVOKE_NOTICE_KEY) !== '1') return false;
    sessionStorage.removeItem(REVOKE_NOTICE_KEY);
    return true;
  } catch {
    return false;
  }
};

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}
